/* eslint-disable react/jsx-props-no-spreading */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import useLocationNext from '../../hooks/useLocationNext';
import usePage from '../../hooks/usePage';
import { getImageAtSize } from '../../lib/utils';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import ProfileForm from '../forms/Profile';
import PageTitle from '../partials/PageTitle';

import styles from '../../../styles/pages/profile.module.scss';

function ProfilePage() {
    const { page } = usePage();
    const [, navigate] = useLocation();
    const url = useUrlGenerator();
    const themeClassName = useThemeClassName(styles);

    const {
        title = null,
        description = null,
        subtitle = null,
        footer = null,
        withEmailForm = false,
        submitLabel = null,
        form = null,
        headerImage = null,
        blocks = null,
    } = page || {};

    const headerBlocks =
        blocks !== null
            ? blocks.filter(({ placement = 'header' }) => (placement || 'header') === 'header')
            : null;

    const { url: imageUrl = null } = getImageAtSize(headerImage) || {};

    const next = useLocationNext();
    const { profileCompleted = null } = useUser();
    const isCreate =
        !profileCompleted || (next !== null && next.length > 0 && next === url('play'));

    const onSuccess = useCallback(() => {
        setTimeout(() => {
            if (next !== null && next.length > 0) {
                navigate(next);
            } else {
                navigate(url('play'));
            }
        }, 300);
    }, [navigate, url, next]);

    const finalTitle = title || (
        <FormattedMessage defaultMessage="Edit your profile" description="Page title" />
    );

    return (
        <div className={classNames([styles.container, themeClassName])}>
            {headerBlocks !== null && headerBlocks.length > 0 ? (
                <Blocks
                    isContainer
                    className={classNames([styles.blocks, 'page-header'])}
                    containerClassName={styles.blocksInner}
                    blocks={headerBlocks}
                />
            ) : (
                <div className={classNames([styles.header, 'page-header'])}>
                    {imageUrl !== null ? (
                        <img src={imageUrl} className={styles.image} alt="Splash" />
                    ) : null}
                    {subtitle === null && finalTitle !== null ? (
                        <PageTitle title={finalTitle} className={styles.title} />
                    ) : null}
                    {subtitle !== null ? (
                        <PageTitle title={subtitle} className={styles.title} />
                    ) : null}
                    {description !== null ? (
                        <div
                            className={styles.description}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    ) : null}
                </div>
            )}
            <div className={styles.inner}>
                {form !== null ? (
                    <ProfileForm
                        onSuccess={onSuccess}
                        withEmailForm={withEmailForm}
                        isCreate={isCreate}
                        footerText={footer}
                        {...form}
                        submitLabel={submitLabel || form?.submitLabel}
                    />
                ) : null}
            </div>
        </div>
    );
}

export default ProfilePage;
